// Bacheca dell'annuncio (#974): il blocco `board` che il server
// (newarc-cloud-functions #660, trigger syncImmaginacasaAdBoard) scrive su
// websiteAdsV2/{adId} —
//   board: {
//     items: [                       // dal più recente, max 20
//       { id, kind: 'post', at, editedAt, text, photos: [{ path, url, thumbUrl, blurhash, variants }] },
//       { id, kind: 'price_drop', at, from, to },
//       { id, kind: 'status', at, from, to },   // negotiation | sold | available
//     ],
//     lastActivityAt: int ms | null,  // max(at) degli items
//     builtAt: int ms,
//   } | null
// Un annuncio senza `board` (copie scritte prima del blocco) non ha novità:
// la pagina e le card restano come prima. La lettura è difensiva: voci di un
// tipo sconosciuto o senza i campi che servono a mostrarle si scartano, mai
// un errore. Contratto completo: docs/websiteAdsV2.md.

import { MARKET_AVAILABLE, MARKET_NEGOTIATION, MARKET_SOLD, PRICE_DROP_LABEL } from './market';

export const BOARD_POST = 'post';
export const BOARD_PRICE_DROP = 'price_drop';
export const BOARD_STATUS = 'status';

// Voci mostrate prima di «Mostra tutte».
export const BOARD_PREVIEW_COUNT = 5;
// «Aggiornato N giorni fa» sulle card dei risultati, solo se recente.
export const BOARD_UPDATED_WINDOW_DAYS = 30;

const DAY_MS = 24 * 60 * 60 * 1000;
const STATUSES = [MARKET_AVAILABLE, MARKET_NEGOTIATION, MARKET_SOLD];

// Testi delle voci: passano dal dizionario (src/i18n/locales/en.json) quando
// arrivano nella pagina.
export const BOARD_LABELS = {
  post: 'Novità',
  priceDrop: PRICE_DROP_LABEL,
  [MARKET_NEGOTIATION]: 'In trattativa',
  [MARKET_SOLD]: 'Venduto',
  [MARKET_AVAILABLE]: 'Di nuovo disponibile',
  published: 'Pubblicato su Immagina Casa',
  today: 'Oggi',
  yesterday: 'Ieri',
};

function isObject(v) {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

function finite(v) {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function positive(v) {
  const n = finite(v);
  return n !== null && n > 0 ? n : null;
}

// Foto di un post: la stessa voce immagine delle gallerie dell'annuncio
// (scala WebP/AVIF in `variants`). Serve almeno un url da mostrare.
function normalizePhoto(raw) {
  if (!isObject(raw)) return null;
  const url = typeof raw.url === 'string' ? raw.url : '';
  const thumbUrl = typeof raw.thumbUrl === 'string' ? raw.thumbUrl : '';
  if (!url && !thumbUrl) return null;
  return {
    path: typeof raw.path === 'string' ? raw.path : '',
    url: url || thumbUrl,
    thumbUrl: thumbUrl || url,
    blurhash: typeof raw.blurhash === 'string' ? raw.blurhash : '',
    variants: Array.isArray(raw.variants)
      ? raw.variants.filter((v) => isObject(v) && typeof v.url === 'string' && v.url && finite(v.w))
      : [],
  };
}

function normalizeItem(raw, index) {
  if (!isObject(raw)) return null;
  const at = positive(raw.at);
  if (at === null) return null;
  const id = typeof raw.id === 'string' && raw.id ? raw.id : `${raw.kind}-${at}-${index}`;

  if (raw.kind === BOARD_POST) {
    const text = typeof raw.text === 'string' ? raw.text.trim() : '';
    const photos = Array.isArray(raw.photos) ? raw.photos.map(normalizePhoto).filter(Boolean) : [];
    if (!text && !photos.length) return null;
    const editedAt = positive(raw.editedAt);
    return {
      id,
      kind: BOARD_POST,
      at,
      // Una modifica conta solo se è davvero dopo la pubblicazione.
      editedAt: editedAt !== null && editedAt > at ? editedAt : null,
      text,
      photos,
      from: null,
      to: null,
    };
  }

  if (raw.kind === BOARD_PRICE_DROP) {
    const from = positive(raw.from);
    const to = positive(raw.to);
    // Solo un ribasso vero: entrambi i prezzi e il nuovo più basso.
    if (from === null || to === null || to >= from) return null;
    return { id, kind: BOARD_PRICE_DROP, at, editedAt: null, text: '', photos: [], from, to };
  }

  if (raw.kind === BOARD_STATUS) {
    // Uno stato che questa versione del sito non conosce non si mostra.
    if (!STATUSES.includes(raw.to)) return null;
    const from = STATUSES.includes(raw.from) ? raw.from : null;
    if (from === raw.to) return null;
    return { id, kind: BOARD_STATUS, at, editedAt: null, text: '', photos: [], from, to: raw.to };
  }

  // Tipi futuri (open house, …): ignorati finché il sito non li conosce.
  return null;
}

// Il blocco letto in modo tollerante: { items, lastActivityAt }. Voci dal più
// recente (a parità di data resta l'ordine del server); `lastActivityAt` è
// quello del server se valido, altrimenti la data della voce più recente;
// null senza voci.
export function normalizeBoard(raw) {
  const b = isObject(raw) ? raw : {};
  const items = (Array.isArray(b.items) ? b.items : [])
    .map((item, i) => normalizeItem(item, i))
    .filter(Boolean)
    .map((item, i) => ({ item, i }))
    .sort((a, b2) => b2.item.at - a.item.at || a.i - b2.i)
    .map(({ item }) => item);
  const latest = items.length ? items[0].at : null;
  const declared = positive(b.lastActivityAt);
  return {
    items,
    lastActivityAt: items.length ? Math.max(declared || 0, latest) : null,
  };
}

// Tipo della voce: «Novità», «Prezzo ribassato», «In trattativa», «Venduto»,
// «Di nuovo disponibile».
export function boardItemLabel(item) {
  if (!item) return '';
  if (item.kind === BOARD_POST) return BOARD_LABELS.post;
  if (item.kind === BOARD_PRICE_DROP) return BOARD_LABELS.priceDrop;
  if (item.kind === BOARD_STATUS) return BOARD_LABELS[item.to] || '';
  return '';
}

// Giorni di calendario (ora locale) tra `at` e `now`: 0 oggi, 1 ieri. Una
// data nel futuro (orologi non allineati) vale oggi.
export function calendarDaysAgo(at, now = Date.now()) {
  const t = finite(at);
  if (t === null) return null;
  const startOf = (ms) => {
    const d = new Date(ms);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  };
  // Math.round assorbe l'ora in più o in meno dei cambi d'ora.
  return Math.max(0, Math.round((startOf(now) - startOf(t)) / DAY_MS));
}

// Data di una voce della bacheca: «Oggi», «Ieri», altrimenti «12 ott» (con
// l'anno se non è quello in corso).
export function boardDateLabel(at, now = Date.now(), locale = 'it-IT') {
  const days = calendarDaysAgo(at, now);
  if (days === null) return '';
  if (days === 0) return BOARD_LABELS.today;
  if (days === 1) return BOARD_LABELS.yesterday;
  const d = new Date(at);
  const opts = { day: 'numeric', month: 'short' };
  if (d.getFullYear() !== new Date(now).getFullYear()) opts.year = 'numeric';
  // «12 ott.» in alcune versioni di ICU: il punto dell'abbreviazione non serve.
  return d.toLocaleDateString(locale, opts).replace(/\.(?=\s|$)/g, '');
}

// Giorni dall'ultima attività per la riga «Aggiornato …» delle card: 0 oggi,
// 1 ieri, N; null senza attività o se più vecchia di 30 giorni.
export function updatedDaysAgo(lastActivityAt, now = Date.now()) {
  const at = positive(lastActivityAt);
  if (at === null) return null;
  const days = calendarDaysAgo(at, now);
  return days <= BOARD_UPDATED_WINDOW_DAYS ? days : null;
}

// «250.000 €» — prezzo di una voce di ribasso.
export function formatBoardPrice(value) {
  const n = finite(value);
  return n === null ? '' : `${Math.round(n).toLocaleString('it-IT')} €`;
}
